import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import type { FC, MouseEvent } from 'react';

export interface OptionType {
  value: string;
  label: string;
}

type SelectProps = {
  options: OptionType[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  menuClassName?: string;
  // A document event to close the menu on, besides a click outside.
  closeOn?: string;
};

const Value: FC<SelectProps> = ({ options, value, label }) => (
  <span>
    <span className="select_label">{`${label}: `}</span>
    <span className="select_value">{options.find((o) => o.value === value)?.label || value}</span>
  </span>
);

const Select: FC<SelectProps> = (props) => {
  const { options, value, onChange, menuClassName = '', closeOn } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | globalThis.MouseEvent) => {
      if (containerRef.current && !(containerRef.current as HTMLElement).contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!closeOn) return undefined;

    const close = () => setMenuOpen(false);
    document.addEventListener(closeOn, close);
    return () => document.removeEventListener(closeOn, close);
  }, [closeOn]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((open) => !open)}
        className="f-provider_button xo-button xo-button_faded xo-button_icon-text xo-button_align-left"
      >
        <Value {...props} />
      </div>

      {menuOpen && (
        <div className={`${menuClassName} f-type`} role="listbox">
          {options.map((option) => {
            const isSelected = value === option.value;
            return (
              // eslint-disable-next-line jsx-a11y/interactive-supports-focus
              <div
                className="f-type_option"
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setMenuOpen(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                <span>{option.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Select;
